import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  Briefcase, TrendingUp, Users, Shield, Search, Filter, 
  DollarSign, BarChart3, CheckCircle, Clock, Star, Eye,
  FileText, Calendar, ArrowRight, Building2, Target, PieChart,
  AlertTriangle, Wallet, Globe, Award
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InvestorPortal = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("dealflow");
  const [matchedStartups, setMatchedStartups] = useState([]);
  const [investments, setInvestments] = useState({ investments: [], portfolio_summary: {} });
  const [dueDiligence, setDueDiligence] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [industryFilter, setIndustryFilter] = useState("all");
  const [stageFilter, setStageFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [matchesRes, investmentsRes] = await Promise.all([
        axios.get(`${API}/matching/startups`, { headers }).catch(() => ({ data: { matches: [] } })),
        axios.get(`${API}/investments/history`, { headers }).catch(() => ({ data: { investments: [], portfolio_summary: {} } }))
      ]);

      setMatchedStartups(matchesRes.data.matches || []);
      setInvestments(investmentsRes.data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const initializeDueDiligence = async (startupId) => {
    try {
      await axios.post(`${API}/due-diligence/initialize/${startupId}`, {}, { headers });
      toast({ title: "Due Diligence Initialized", description: "Checklist created with 35 items" });
      fetchDueDiligence(startupId);
    } catch (e) {
      toast({ title: "Error", description: e.response?.data?.detail || "Failed to initialize", variant: "destructive" });
    }
  };

  const fetchDueDiligence = async (startupId) => {
    try {
      const res = await axios.get(`${API}/due-diligence/${startupId}`, { headers });
      setDueDiligence(prev => ({ ...prev, [startupId]: res.data }));
    } catch (e) {
      console.error(e);
    }
  };

  const filteredStartups = matchedStartups.filter(match => {
    const startup = match.startup;
    if (industryFilter !== "all" && startup.industry !== industryFilter) return false;
    if (stageFilter !== "all" && startup.stage !== stageFilter) return false;
    if (searchQuery && !startup.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const industries = [...new Set(matchedStartups.map(m => m.startup?.industry).filter(Boolean))];
  const stages = [...new Set(matchedStartups.map(m => m.startup?.stage).filter(Boolean))];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900">
      <Header />

      {/* Header Banner */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white py-8">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Briefcase className="h-8 w-8" />
                Investor Portal
              </h1>
              <p className="opacity-90 mt-1">AI-powered deal flow, due diligence & portfolio management</p>
            </div>
            <div className="flex gap-3">
              <Button onClick={() => navigate('/investor-pipeline')} variant="secondary">
                <Target className="h-4 w-4 mr-2" /> Pipeline
              </Button>
              <Button onClick={() => setActiveTab('portfolio')} className="bg-white text-blue-600">
                <PieChart className="h-4 w-4 mr-2" /> Portfolio
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        {/* Portfolio Summary */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <Card className="bg-slate-800 border-slate-700 text-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <DollarSign className="h-8 w-8 text-green-400" />
                <div>
                  <p className="text-sm text-slate-400">Total Invested</p>
                  <p className="text-2xl font-bold">${investments.portfolio_summary?.total_invested?.toLocaleString() || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700 text-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Building2 className="h-8 w-8 text-blue-400" />
                <div>
                  <p className="text-sm text-slate-400">Active Investments</p>
                  <p className="text-2xl font-bold">{investments.portfolio_summary?.active_count || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700 text-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <TrendingUp className="h-8 w-8 text-emerald-400" />
                <div>
                  <p className="text-sm text-slate-400">Total Exits</p>
                  <p className="text-2xl font-bold">{investments.portfolio_summary?.exited_count || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700 text-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Wallet className="h-8 w-8 text-amber-400" />
                <div>
                  <p className="text-sm text-slate-400">Exit Value</p>
                  <p className="text-2xl font-bold">${investments.portfolio_summary?.total_exit_value?.toLocaleString() || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700 text-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Award className="h-8 w-8 text-purple-400" />
                <div>
                  <p className="text-sm text-slate-400">Overall Multiple</p>
                  <p className="text-2xl font-bold">{investments.portfolio_summary?.overall_multiple?.toFixed(1) || 0}x</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-800 border-slate-700 mb-6">
            <TabsTrigger value="dealflow" className="data-[state=active]:bg-blue-600">Deal Flow</TabsTrigger>
            <TabsTrigger value="duediligence" className="data-[state=active]:bg-blue-600">Due Diligence</TabsTrigger>
            <TabsTrigger value="portfolio" className="data-[state=active]:bg-blue-600">Portfolio</TabsTrigger>
          </TabsList>

          {/* DEAL FLOW TAB */}
          <TabsContent value="dealflow">
            {/* Filters */}
            <Card className="bg-slate-800 border-slate-700 mb-6">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Search startups..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 bg-slate-700 border-slate-600 text-white"
                      />
                    </div>
                  </div>
                  <Select value={industryFilter} onValueChange={setIndustryFilter}>
                    <SelectTrigger className="w-[180px] bg-slate-700 border-slate-600 text-white">
                      <SelectValue placeholder="Industry" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Industries</SelectItem>
                      {industries.map(ind => (
                        <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={stageFilter} onValueChange={setStageFilter}>
                    <SelectTrigger className="w-[180px] bg-slate-700 border-slate-600 text-white">
                      <SelectValue placeholder="Stage" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Stages</SelectItem>
                      {stages.map(stage => (
                        <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredStartups.map(({ startup, match_score, reasons }) => (
                <Card key={startup.id} className="bg-slate-800 border-slate-700 text-white overflow-hidden hover:border-blue-500 transition-colors">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{startup.name}</CardTitle>
                        <CardDescription className="text-slate-400">{startup.industry}</CardDescription>
                      </div>
                      <div className="text-right">
                        <div className={`text-2xl font-bold ${
                          match_score >= 70 ? 'text-green-400' :
                          match_score >= 50 ? 'text-amber-400' : 'text-slate-400'
                        }`}>
                          {match_score}%
                        </div>
                        <p className="text-xs text-slate-500">match</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 mb-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-400">Stage</span>
                        <Badge variant="secondary">{startup.stage}</Badge>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-400">Governance</span>
                        <div className="flex items-center gap-2">
                          <Progress value={startup.governance_score * 10} className="w-20 h-2" />
                          <span>{startup.governance_score}/10</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-400">Location</span>
                        <span className="flex items-center gap-1">
                          <Globe className="h-3 w-3" /> {startup.location}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-1 mb-4">
                      {reasons.map((reason, i) => (
                        <Badge key={i} variant="outline" className="text-xs border-slate-600">
                          {reason}
                        </Badge>
                      ))}
                    </div>

                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        className="flex-1 bg-blue-600 hover:bg-blue-700"
                        onClick={() => {
                          initializeDueDiligence(startup.id);
                          setActiveTab('duediligence');
                        }}
                      >
                        Start DD
                      </Button>
                      <Button size="sm" variant="outline" className="border-slate-600">
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {filteredStartups.length === 0 && (
              <Card className="bg-slate-800 border-slate-700 p-12 text-center">
                <Building2 className="h-16 w-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No Matching Startups</h3>
                <p className="text-slate-400">Try adjusting your filters or check back later for new opportunities</p>
              </Card>
            )}
          </TabsContent>

          {/* DUE DILIGENCE TAB */}
          <TabsContent value="duediligence">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Due Diligence Center
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Comprehensive DD checklist covering legal, financial, technical, market & team analysis
                </CardDescription>
              </CardHeader>
              <CardContent>
                {Object.keys(dueDiligence).length > 0 ? (
                  <div className="space-y-6">
                    {Object.entries(dueDiligence).map(([startupId, dd]) => (
                      <div key={startupId} className="border border-slate-700 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-lg font-semibold text-white">Startup DD</h4>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-2xl font-bold text-blue-400">
                                {dd.summary?.completion_percentage?.toFixed(0) || 0}%
                              </p>
                              <p className="text-xs text-slate-400">Complete</p>
                            </div>
                          </div>
                        </div>
                        
                        <Progress value={dd.summary?.completion_percentage || 0} className="h-2 mb-4" />
                        
                        <div className="grid grid-cols-5 gap-4 text-center">
                          {['legal', 'financial', 'technical', 'market', 'team'].map(cat => {
                            const items = dd.by_category?.[cat] || [];
                            const completed = items.filter(i => i.status === 'completed').length;
                            return (
                              <div key={cat} className="p-3 bg-slate-700 rounded-lg">
                                <p className="text-xs text-slate-400 capitalize">{cat}</p>
                                <p className="text-lg font-bold text-white">{completed}/{items.length}</p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FileText className="h-16 w-16 text-slate-600 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-white mb-2">No Active Due Diligence</h3>
                    <p className="text-slate-400 mb-4">Start DD on a startup from the Deal Flow tab</p>
                    <Button onClick={() => setActiveTab('dealflow')} className="bg-blue-600">
                      Browse Deal Flow
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* PORTFOLIO TAB */}
          <TabsContent value="portfolio">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <PieChart className="h-5 w-5" />
                  Investment Portfolio
                </CardTitle>
              </CardHeader>
              <CardContent>
                {investments.investments?.length > 0 ? (
                  <div className="space-y-4">
                    {investments.investments.map((inv) => (
                      <div key={inv.id} className="flex items-center justify-between p-4 bg-slate-700 rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className={`p-3 rounded-full ${
                            inv.status === 'active' ? 'bg-green-500/20' :
                            inv.status === 'exited' ? 'bg-blue-500/20' : 'bg-slate-500/20'
                          }`}>
                            <Building2 className={`h-6 w-6 ${
                              inv.status === 'active' ? 'text-green-400' :
                              inv.status === 'exited' ? 'text-blue-400' : 'text-slate-400'
                            }`} />
                          </div>
                          <div>
                            <h4 className="font-semibold text-white">{inv.startup_name}</h4>
                            <p className="text-sm text-slate-400">{inv.round_name} • {inv.investment_date}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-white">${inv.amount?.toLocaleString()}</p>
                          {inv.status === 'exited' && inv.exit_multiple && (
                            <Badge className="bg-green-500">{inv.exit_multiple.toFixed(1)}x return</Badge>
                          )}
                          {inv.status === 'active' && (
                            <Badge variant="outline" className="border-green-500 text-green-400">Active</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Wallet className="h-16 w-16 text-slate-600 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-white mb-2">No Investments Yet</h3>
                    <p className="text-slate-400 mb-4">Start investing to track your portfolio here</p>
                    <Button onClick={() => setActiveTab('dealflow')} className="bg-blue-600">
                      Explore Opportunities
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default InvestorPortal;
